import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { ResponsiveContainer, ResponsiveContent } from '../ui/ResponsiveLayout';
import BrandSymbol from '../ui/BrandSymbol';
import { API_BASE_URL } from '../../config/api';


const Frame250: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session, updateTaskProgress, extendSession } = useSessionPersistence();
  const { status: subStatus, hasAccess, refetch: refetchSubscription } = useSubscriptionStatus();
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'processing' | 'success' | 'error'>('pending');
  const [verificationMessage, setVerificationMessage] = useState('Processing your payment...');
  const verificationInProgressRef = React.useRef(false);
  const verifiedSessionIdRef = React.useRef<string | null>(null);
  const [customerSessionSecret, setCustomerSessionSecret] = useState<string | null>(null);
  const [pricingTableReady, setPricingTableReady] = useState(false);
  
  // When returning from Stripe checkout with a session_id, the user has been
  // away from the app (on Stripe's hosted page). Reset the idle/absolute
  // session timers so the user isn't immediately logged out mid-verification.
  // Completing a payment is not an idle condition.
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      console.log('[Frame250] Returned from Stripe checkout — resetting session timers');
      extendSession();
    }
  }, []); // Run once on mount

  // Use test publishable key for preprod, production key for production
  // Fallback to hardcoded key if environment variables not set
  const stripePublishableKey = import.meta.env.VITE_ENVIRONMENT === 'production'
    ? (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_live_51RqnWl3TItGOMcoaEDoTopR1u3UULXh0xUDhtWcUkbUxCJ1ouyDQ6YOZvA9NNhc6YluPweic7mqejqGNL8LLfhZF00CjkiSuGy')
    : (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_TEST || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_live_51RqnWl3TItGOMcoaEDoTopR1u3UULXh0xUDhtWcUkbUxCJ1ouyDQ6YOZvA9NNhc6YluPweic7mqejqGNL8LLfhZF00CjkiSuGy');

  // Use test pricing table for preprod, production pricing table for production
  const stripePricingTableId = import.meta.env.VITE_ENVIRONMENT === 'production'
    ? 'prctbl_1T03UW3TItGOMcoaADNlHdJ8'  // Production pricing table
    : 'prctbl_1TAdHlQMslSH8ggyFjjxXiay'; // Test pricing table

  // Fetch a Stripe Customer Session on mount so the pricing table reuses
  // the existing Stripe customer instead of creating a duplicate.
  // If the user has no Stripe customer yet (first purchase), we skip this
  // and let the pricing table create one normally.
  useEffect(() => {
    const fetchCustomerSession = async () => {
      const token = localStorage.getItem('account-token');
      if (!token) {
        setPricingTableReady(true);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/stripe/customer-session`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.clientSecret) {
            console.log('[Frame250] Got customer session secret for existing Stripe customer');
            setCustomerSessionSecret(data.clientSecret);
          } else {
            console.log('[Frame250] No existing Stripe customer — first-time checkout');
          }
        }
      } catch (error) {
        console.error('[Frame250] Failed to fetch customer session:', error);
        // Non-fatal — pricing table will just create a new customer
      } finally {
        setPricingTableReady(true);
      }
    };

    fetchCustomerSession();
  }, []);

  // Define handlePaymentSuccess BEFORE useEffect that calls it
  const handlePaymentSuccess = useCallback(async (sessionId: string) => {
    // Prevent duplicate verification requests
    if (verificationInProgressRef.current || verifiedSessionIdRef.current === sessionId) {
      console.log('[Frame250] Verification already in progress or completed for this session');
      return;
    }

    verificationInProgressRef.current = true;
    setPaymentStatus('processing');
    setVerificationMessage('Verifying your payment with Stripe...');
    
    const token = localStorage.getItem('account-token');
    if (!token) {
      console.error('[Frame250] No auth token found');
      setPaymentStatus('error');
      verificationInProgressRef.current = false;
      return;
    }

    try {
      // Call backend to verify Stripe session
      const response = await fetch(`${API_BASE_URL}/api/stripe/verify-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sessionId })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setVerificationMessage('Payment verified! Activating your subscription...');
        
        // Mark this session as verified
        verifiedSessionIdRef.current = sessionId;
        
        // Save payment status to session
        updateTaskProgress('payment', {
          status: 'completed',
          completedAt: new Date().toISOString(),
          paymentMethod: 'stripe',
          amount: 99,
          currency: 'USD',
          billingCycle: 'annual',
          verified: true,
          stripeSubscriptionId: data.subscriptionId
        });

        // Force refresh subscription status to prevent redirect loop
        console.log('[Frame250] Refreshing subscription status after payment verification');
        await refetchSubscription();
        console.log('[Frame250] Subscription status refresh completed');
        
        setVerificationMessage('Payment verified! Your subscription is now active.');
        setPaymentStatus('success');
      } else {
        console.error('[Frame250] Payment verification failed:', data);
        setVerificationMessage(data.message || 'Payment verification failed');
        setPaymentStatus('error');
      }
    } catch (error) {
      console.error('[Frame250] Verification error:', error);
      setVerificationMessage('Unable to verify payment. Please contact support.');
      setPaymentStatus('error');
    } finally {
      verificationInProgressRef.current = false;
    }
  }, [updateTaskProgress, setVerificationMessage, refetchSubscription]);

  // Check for payment success from URL params (after Stripe redirect)
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');

    if (sessionId && sessionId !== verifiedSessionIdRef.current) {
      // Verify the session with Stripe backend
      handlePaymentSuccess(sessionId);
    } else if (canceled === 'true') {
      setPaymentStatus('error');
    }
  }, [searchParams, handlePaymentSuccess]);

  // Fallback: if user returns without session_id but already has an active subscription
  // (e.g., Stripe redirect didn't include session_id, or webhook already activated it),
  // show success state instead of the pricing table
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId && paymentStatus === 'pending' && hasAccess && (subStatus === 'active' || subStatus === 'trialing')) {
      console.log('[Frame250] Fallback: User already has active subscription, showing success state');
      setPaymentStatus('success');
      setVerificationMessage('Your subscription is active.');
    }
  }, [subStatus, hasAccess, paymentStatus, searchParams]);

  const handleContinue = () => {
    navigate('/frame-134'); // Navigate to onboarding video
  };



  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center">
              <BrandSymbol size="4xl" variant="dark" showText={true} />
            </div>
            <h1 className="text-3xl font-bold text-stone-900 mb-2">
              Secure Your Estate Succession Plan
            </h1>
          </div>

          {/* Stripe Pricing Table */}
          {paymentStatus === 'pending' && pricingTableReady && (
            <div className="bg-white border border-stone-200 rounded-xl p-6 mb-8">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-stone-900 mb-2">
                  Choose Your Plan
                </h3>
                <p className="text-stone-600">
                  Secure payment powered by Stripe
                </p>
              </div>

              <stripe-pricing-table
                pricing-table-id={stripePricingTableId}
                publishable-key={stripePublishableKey}
                {...(customerSessionSecret
                  ? { 'customer-session-client-secret': customerSessionSecret }
                  : { 'customer-email': session?.email }
                )}
              />

              <div className="mt-6 text-center">
                <p className="text-xs text-stone-500">
                  Your payment information is secure and encrypted
                </p>
              </div>
            </div>
          )}

          {/* Loading state while fetching customer session */}
          {paymentStatus === 'pending' && !pricingTableReady && (
            <div className="bg-white border border-stone-200 rounded-xl p-6 mb-8 text-center">
              <div className="animate-pulse">
                <div className="h-4 bg-stone-200 rounded w-1/3 mx-auto mb-4"></div>
                <div className="h-40 bg-stone-100 rounded"></div>
              </div>
            </div>
          )}

          {paymentStatus === 'processing' && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="animate-spin w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-blue-800 mb-2">
                {verificationMessage}
              </h3>
              <p className="text-blue-700 text-sm">
                Please wait while we activate your subscription...
              </p>
            </div>
          )}

          {paymentStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-green-800 mb-2">
                Payment Successful!
              </h3>
              <p className="text-green-700 mb-4">
                Your annual subscription is now active. Return to your dashboard to continue.
              </p>
              <button
                onClick={handleContinue}
                className="bg-green-600 text-white py-2 px-6 rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                Continue to Dashboard
              </button>
            </div>
          )}

          {paymentStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-red-800 mb-2">
                Payment Failed
              </h3>
              <p className="text-red-700 mb-4">
                {verificationMessage}
              </p>
              <button
                onClick={() => setPaymentStatus('pending')}
                className="bg-red-600 text-white py-2 px-6 rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Security Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="font-medium text-blue-900 mb-1">Secure Payment</h4>
                <p className="text-sm text-blue-700">
                  Your payment information is encrypted and secure. We use Stripe for payment processing and never store your card details.
                </p>
              </div>
            </div>
          </div>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default Frame250;
